'use client';

import { Html } from '@react-three/drei';
import { useTheme } from 'next-themes';
import { Thermometer, Droplets, Wind, Sun, Activity } from 'lucide-react';

interface SensorIndicatorProps {
  sensor: any;
  isSelected: boolean;
  onClick: () => void;
  metric: string;
}

export default function SensorIndicator({ sensor, isSelected, onClick, metric }: SensorIndicatorProps) {
  const { theme } = useTheme();

  const getStatusColor = () => {
    if (sensor.temperature > 25 || sensor.temperature < 15) return '#ef4444';
    if (sensor.humidity > 70 || sensor.humidity < 30) return '#f59e0b';
    return '#10b981';
  };

  const sensorColor = isSelected ? '#3b82f6' : getStatusColor();
  const emissiveColor = isSelected ? '#1e40af' : '#000000';
  const emissiveIntensity = isSelected ? 0.3 : 0;

  return (
    <group position={sensor.position}>
      <mesh
        onClick={onClick}
        onPointerOver={(e) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'auto';
        }}
      >
        <cylinderGeometry args={[0.15, 0.15, 0.3, 16]} />
        <meshStandardMaterial color={sensorColor} emissive={emissiveColor} emissiveIntensity={emissiveIntensity} />
      </mesh>

      {/* Status indicator */}
      <mesh position={[0, 0.3, 0]}>
        <sphereGeometry args={[0.05, 16, 16]} />
        <meshBasicMaterial color={getStatusColor()} />
      </mesh>

      <Html position={[0, 0.8, 0]} center distanceFactor={8} occlude>
        <div
          className={`border shadow-lg rounded-lg p-3 min-w-[140px] ${
            theme === 'dark' ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        >
          <div className="text-xs font-semibold mb-2">{sensor.name}</div>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">
                <Thermometer className="w-3 h-3 text-red-500" />
                Temp
              </span>
              <span className="font-semibold">{sensor.temperature}°C</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">
                <Droplets className="w-3 h-3 text-blue-500" />
                Humidity
              </span>
              <span className="font-semibold">{sensor.humidity}%</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">
                <Wind className="w-3 h-3 text-green-500" />
                Air Quality
              </span>
              <span className="font-semibold">{sensor.airQuality}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">
                <Sun className="w-3 h-3 text-yellow-500" />
                Light
              </span>
              <span className="font-semibold">{sensor.light}lux</span>
            </div>
          </div>
          {sensor.motion && (
            <div className="mt-2 flex items-center gap-1 text-xs text-orange-600">
              <Activity className="w-3 h-3" />
              Motion Detected
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}
